import { useMemo } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { buildHighlightCandidates } from "@/shared/utils/highlightMatches";
import { formatListName } from "@/shared/utils/formatListName";

/**
 * The order of this widget's highlighted lists, and the way to change it.
 *
 * When a row matches more than one highlighted list, the first in this order
 * paints it. The order belongs to this widget — the same lists can be the
 * other way round in another widget — so it is set here, beside the lists it
 * ranks, rather than on the lists. Only shown once there are two to rank.
 */
export default function HighlightOrderSection({
  order,
  onMove,
}: {
  order: string[];
  onMove: (key: string, delta: -1 | 1) => void;
}) {
  const { portfolios } = usePortfoliosCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const lists = useMemo(
    () => buildHighlightCandidates(order, portfolios, queriesQuery.data),
    [order, portfolios, queriesQuery.data]
  );

  if (lists.length < 2) return null;

  return (
    <div className="border-b border-gray-700/30 px-3 py-2">
      <div className="text-2xs uppercase tracking-wider text-gray-500">
        When more than one list matches
      </div>
      <p className="mb-1.5 mt-0.5 text-2xs text-gray-500">
        The top list&apos;s colour is used. Every list that matches is still
        named.
      </p>
      <ol className="space-y-0.5">
        {lists.map((list, i) => {
          const name = formatListName(list.name);
          return (
            <li key={list.key} className="flex items-center gap-2 text-xs text-gray-200">
              <span className="w-3 text-right text-2xs text-gray-500">{i + 1}</span>
              <span
                className="size-3 flex-shrink-0 rounded-sm border border-gray-600"
                style={{ backgroundColor: list.color }}
                aria-hidden
              />
              <span className="flex-1 truncate">{name}</span>
              <button
                type="button"
                onClick={() => onMove(list.key, -1)}
                disabled={i === 0}
                aria-label={`Move ${name} up`}
                className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-100 disabled:cursor-default disabled:opacity-30"
              >
                <ChevronUpIcon className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onMove(list.key, 1)}
                disabled={i === lists.length - 1}
                aria-label={`Move ${name} down`}
                className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-100 disabled:cursor-default disabled:opacity-30"
              >
                <ChevronDownIcon className="size-3.5" />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
